/**
 * Página de Administração
 * Menu principal de administração do sistema
 * 
 * @author Equipe GestaoeCRM
 * @date 26/09/2025
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Settings, Shield, Users, Database, BarChart3 } from 'lucide-react';

const AdministrationPage: React.FC = () => {
  const adminModules = [
    {
      title: 'Gerenciar Permissões',
      description: 'Configure permissões de menu para cada role do sistema',
      icon: Shield,
      href: '/administration/permissions',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-gradient-to-r from-primary to-secondary'
    },
    {
      title: 'Gestão de Usuários',
      description: 'Administre usuários, roles e permissões individuais',
      icon: Users,
      href: '/users',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600'
    },
    {
      title: 'Configurações do Sistema',
      description: 'Configure parâmetros gerais do sistema',
      icon: Settings,
      href: '/administration/settings',
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600'
    },
    {
      title: 'Banco de Dados',
      description: 'Gerencie migrações e dados do sistema',
      icon: Database,
      href: '/administration/database',
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600'
    },
    {
      title: 'Relatórios de Sistema',
      description: 'Relatórios administrativos e auditoria',
      icon: BarChart3,
      href: '/administration/reports',
      color: 'bg-indigo-500',
      hoverColor: 'hover:bg-indigo-600'
    }
  ];

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-text-primary mb-2">
          Administração do Sistema
        </h1>
        <p className="text-text-secondary">
          Gerencie configurações, permissões e funcionalidades administrativas do sistema.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminModules.map((module, index) => {
          const IconComponent = module.icon;
          return (
            <Link
              key={index}
              to={module.href}
              className="group bg-background-glass backdrop-blur-glass rounded-glass shadow-md hover:shadow-glass transition-all duration-300-all duration-200 border border-border-glass hover:border-border-glass"
            >
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className={`p-3 rounded-glass ${module.color} ${module.hoverColor} transition-all duration-300-all duration-300 duration-200`}>
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-text-primary ml-3 group-hover:text-text-secondary">
                    {module.title}
                  </h3>
                </div>
                <p className="text-text-secondary text-sm leading-relaxed">
                  {module.description}
                </p>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-glass p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-2">
          ℹ️ Informações Importantes
        </h3>
        <ul className="text-secondary text-sm space-y-2">
          <li>• Todas as alterações administrativas são registradas em logs de auditoria</li>
          <li>• Faça backup do banco de dados antes de alterações críticas</li>
          <li>• Teste as configurações em ambiente de desenvolvimento primeiro</li>
          <li>• Mantenha documentação atualizada das mudanças realizadas</li>
        </ul>
      </div>
    </div>
  );
};

export default AdministrationPage;
